import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useSite, wa } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = (hasError) =>
  `w-full rounded-xl border bg-cream-100/70 px-4 py-3.5 text-sm text-charcoal-900 outline-none transition-all duration-300 placeholder:text-charcoal-900/30 focus:ring-2 ${
    hasError
      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
      : "border-charcoal-900/10 focus:border-gold focus:ring-gold/20"
  }`;

export default function ContactPage() {
  const D = useSite();
  const p = D.pages.iletisim;
  const site = D.site;

  const INFO = [
    { icon: MapPin, label: "Adres", value: site.address },
    { icon: Phone, label: "Telefon & WhatsApp", value: site.phone, href: `tel:${site.phoneTel}` },
    { icon: Phone, label: "Sabit Hat", value: site.phone2, href: `tel:${site.phone2Tel}` },
    { icon: Mail, label: "E-posta", value: site.email, href: `mailto:${site.email}` },
    { icon: Clock, label: "Rehberli Tur", value: "Her gün 09.00 – 20.00 (randevulu)" },
  ];

  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Lütfen adınızı ve soyadınızı girin.";
    if (form.phone.replace(/\D/g, "").length < 10) er.phone = "Geçerli bir telefon numarası girin.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Geçerli bir e-posta adresi girin.";
    if (form.message.trim().length < 10) er.message = "Mesajınız en az 10 karakter olmalı.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      toast.error("Lütfen formdaki eksik alanları düzeltin.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mesajınız alındı! En kısa sürede size dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Mesajınız gönderilemedi. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero overline={p.overline} titleLines={p.titleLines} description={p.description} />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <div className="space-y-4">
              {INFO.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.08}>
                  <div
                    data-testid={`contact-info-${i}`}
                    className="group flex items-start gap-5 rounded-3xl border border-charcoal-900/5 bg-white p-6 shadow-premium transition-all duration-500 ease-premium hover:-translate-y-1 hover:border-gold/30 hover:shadow-lift"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold/10 text-gold-dark transition-colors duration-500 group-hover:bg-gold/20">
                      <item.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-accent text-[10px] uppercase tracking-[0.25em] text-charcoal-900/45">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="mt-1.5 block text-sm font-semibold text-charcoal-900 transition-colors duration-300 hover:text-gold-dark"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-1.5 text-sm font-semibold text-charcoal-900">{item.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.35}>
              <a
                data-testid="contact-whatsapp-button"
                href={wa(site)}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] py-4 text-sm font-semibold text-white shadow-premium transition-all duration-500 ease-premium hover:-translate-y-0.5 hover:shadow-lift"
              >
                <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                Hemen WhatsApp'tan Yazın
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="rounded-[2rem] border border-charcoal-900/5 bg-white p-8 shadow-premium md:p-12"
            >
              <h2 className="font-serif text-3xl tracking-tight text-charcoal-900">Mesaj Gönderin</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Formu doldurun, ekibimiz en geç 24 saat içinde size ulaşsın.
              </p>
              <div className="mt-9 grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/55">
                    Ad Soyad
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Ayşe Yılmaz"
                    className={inputCls(errors.name)}
                  />
                  {errors.name && <p data-testid="contact-name-error" className="mt-2 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/55">
                    Telefon
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="05XX XXX XX XX"
                    className={inputCls(errors.phone)}
                  />
                  {errors.phone && <p data-testid="contact-phone-error" className="mt-2 text-xs text-red-500">{errors.phone}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-email" className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/55">
                    E-posta
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="ornek@eposta.com"
                    className={inputCls(errors.email)}
                  />
                  {errors.email && <p data-testid="contact-email-error" className="mt-2 text-xs text-red-500">{errors.email}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/55">
                    Mesajınız
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Merhaba, yurdunuz hakkında bilgi almak istiyorum..."
                    className={`${inputCls(errors.message)} resize-none`}
                  />
                  {errors.message && <p data-testid="contact-message-error" className="mt-2 text-xs text-red-500">{errors.message}</p>}
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-9 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-charcoal-900 py-4 text-sm font-semibold text-white transition-all duration-500 ease-premium hover:-translate-y-0.5 hover:bg-gold-dark hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-12"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    Gönder
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal className="mt-16">
          <div
            data-testid="contact-map"
            className="h-[380px] overflow-hidden rounded-[2rem] border border-charcoal-900/5 shadow-premium md:h-[440px]"
          >
            <iframe
              title="Ada Kız Öğrenci Yurdu Konum"
              src={`https://www.google.com/maps?q=${encodeURIComponent(site.mapQuery)}&output=embed`}
              className="h-full w-full grayscale-[40%] transition-[filter] duration-700 hover:grayscale-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </>
  );
}
